import { ActionReducer } from '@ngrx/store';
import { CLEAR_STATE } from '../actions/clear-state-actions';
import { ENTITY_RESOURCE_CHANGED } from '../actions/entity-resource-actions';

// Carries no PORTAL_STORE key, so combineReducers hands every slice `undefined` and each falls
// back to its own reducer default. Not `undefined` — combineReducers substitutes the store's
// configured initialState for that, and the literal in store/reducers/index.ts has drifted from
// the reducers it restates.
const NO_SLICES = { state: null };

/**
 * Resets every store slice when the Portal moves to a different entity or portal context, and
 * carries the signed-in user across that reset.
 *
 * A caller that is changing who the user is — every district switch, since the user document is
 * district-scoped — dispatches ClearCurrentUser first, which empties the slice so there is
 * nothing here to carry. That has to be dispatched before ClearState, not after.
 */
export function resetStoreState (reducer: ActionReducer<any>): ActionReducer<any> {
  return (state, action: any) => {
    if (!isContextReset(state, action)) return reducer(state, action);

    if (action.type === ENTITY_RESOURCE_CHANGED) console.warn('New entity resource selected: Resetting NgRx Store');

    const outgoingUser = state?.PORTAL_STORE?.currentUser;
    const cleared = reducer(NO_SLICES, action);
    if (!outgoingUser?.userEntity?._id) return cleared;

    // Re-applied to the result, since NO_SLICES is discarded by design.
    return {
      ...cleared,
      PORTAL_STORE: {
        ...cleared.PORTAL_STORE,
        currentUser: outgoingUser,
      },
    };
  };
}

// Compared against the last announcement rather than a loaded document, so it reads the same for
// every entity type. Re-announcing the entity already open is not a change — a shelter
// sub-location switch re-runs the guard for the same entity.
function isContextReset (state: any, action: any): boolean {
  if (action.type === CLEAR_STATE) return true;
  if (action.type !== ENTITY_RESOURCE_CHANGED) return false;

  return action.payload?.resourceId !== state?.PORTAL_STORE?.entityResource?.resourceId;
}
